import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, GraduationCap, Laptop, MessageCircle } from "lucide-react";
import { EXPLORE_PROGRAMS } from "@/data/homeContent";
import { INDUSTRIES, industryPath } from "@/data/industries";
import { getIndustryIcon } from "@/lib/industryIcons";

/** Left column first (top to bottom), then right — extra item goes in the left column when count is odd. */
const INDUSTRIES_SPLIT = Math.ceil(INDUSTRIES.length / 2);
const INDUSTRIES_LEFT = INDUSTRIES.slice(0, INDUSTRIES_SPLIT);
const INDUSTRIES_RIGHT = INDUSTRIES.slice(INDUSTRIES_SPLIT);

const CLOSE_DELAY_MS = 180;

const ITEM_LINK =
  "group flex items-center gap-3 rounded-2xl p-2.5 transition-colors hover:bg-brand-50 focus-visible:bg-brand-50";
const TILE =
  "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-all duration-200 group-hover:scale-105";
const ARROW =
  "h-4 w-4 shrink-0 -translate-x-1 text-brand-500 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100";

function IndustryLink({ industry }) {
  const Icon = getIndustryIcon(industry.icon);
  return (
    <li>
      <Link to={industryPath(industry.id)} className={ITEM_LINK}>
        <span className={`${TILE} ${industry.theme.accentSoft} ${industry.theme.accentHoverBg}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold leading-snug text-ink">{industry.name}</span>
          <span className="block text-xs text-ink/70">{industry.tagline}</span>
        </span>
        <ArrowRight className={ARROW} aria-hidden="true" />
      </Link>
    </li>
  );
}

function ProgramLink({ item }) {
  const Icon = getIndustryIcon(item.icon);
  return (
    <li>
      <Link to={item.to} className="group flex items-center gap-3 rounded-2xl p-2.5 transition-colors hover:bg-white focus-visible:bg-white">
        <span className={`${TILE} border border-brand-200 bg-white text-brand-600 group-hover:border-brand-500 group-hover:bg-brand-500 group-hover:text-white`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold leading-snug text-ink">{item.title}</span>
          <span className="block text-xs text-ink/70">{item.tagline}</span>
        </span>
        <ArrowRight className={ARROW} aria-hidden="true" />
      </Link>
    </li>
  );
}
export function MegaMenu() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  return (
    <div className="relative hidden lg:block">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        onFocus={openMenu}
        onBlur={scheduleClose}
        className="px-4 py-2 min-h-[44px] text-sm font-semibold text-ink hover:text-brand-600 rounded-full hover:bg-brand-50 transition-colors inline-flex items-center gap-1.5"
      >
        <span>Explore</span>
        <ChevronDown className={`w-3 h-3 text-brand-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`mega-menu-panel fixed left-1/2 top-24 w-[1000px] max-w-[calc(100vw-2rem)] z-50 ${open ? "is-open" : ""}`}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        {/* Invisible bridge so the cursor can travel from the nav link to the panel */}
        <div className="h-4" aria-hidden="true" />

        <div className="bg-white rounded-4xl shadow-soft border border-brand-200 p-6 grid grid-cols-12 gap-6 overflow-hidden">
          <div className="col-span-8 space-y-3 border-r-2 border-brand-100 pr-4">
            <div className="flex items-center gap-2 text-brand-600 font-bold text-xs uppercase tracking-wider">
              <Laptop className="w-4 h-4 text-brand-500" />
              Industries
            </div>
            <div className="grid grid-cols-2 gap-x-4 text-sm">
              <ul className="space-y-1">
                {INDUSTRIES_LEFT.map((industry) => (
                  <IndustryLink key={industry.id} industry={industry} />
                ))}
              </ul>
              <ul className="space-y-1">
                {INDUSTRIES_RIGHT.map((industry) => (
                  <IndustryLink key={industry.id} industry={industry} />
                ))}
              </ul>
            </div>
          </div>

          <div className="col-span-4 flex flex-col rounded-3xl bg-brand-50 p-4">
            <div className="flex items-center gap-2 text-brand-600 font-bold text-xs uppercase tracking-wider">
              <GraduationCap className="w-4 h-4 text-brand-500" />
              Programs
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {EXPLORE_PROGRAMS.map((item) => (
                <ProgramLink key={item.title} item={item} />
              ))}
            </ul>
            <Link
              to="/consultation"
              className="mt-auto inline-flex min-h-[44px] items-center gap-2 rounded-full border border-brand-200 bg-white px-4 text-sm font-semibold transition-transform "
            >
              <MessageCircle className="h-4 w-4 text-brand-500" aria-hidden="true" />
              Not sure? Book a free chat
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

